import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Calendar, CreditCard, Settings,
  LogOut, Menu, X, ChevronRight, Clock, Smile, Frown, Meh, Coffee
} from 'lucide-react';
import ForcePasswordReset from '../auth/ForcePasswordReset';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_ICONS = { happy: Smile, tired: Coffee, upset: Frown, calm: Meh };
const MOOD_COLORS = { happy: '#22c55e', tired: '#f59e0b', upset: '#ef4444', calm: '#3b82f6' };
const STATUS_COLORS = { paid: '#22c55e', pending: '#f59e0b', overdue: '#ef4444', checked_in: '#22c55e', checked_out: '#6b7280', absent: '#ef4444' };

export default function ParentPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState(null);

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const stored = localStorage.getItem('auth_user');
    if (!stored) { navigate(`/${localStorage.getItem('tenant_slug') || ''}/login`); return; }
    const u = JSON.parse(stored);
    if (u.role !== 'parent') { navigate(`/${localStorage.getItem('tenant_slug') || ''}/dashboard`); return; }
    setUser(u);
    fetchSummary();
    // Fetch branding
    const tid = localStorage.getItem('tenant_id');
    if (tid) {
      axios.get(`${API}/branding/${tid}`).then(r => setBranding(r.data)).catch(() => {});
    }
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/daycare/parent-portal/summary`, { headers });
      setSummary(res.data);
    } catch (err) {
      console.error('Summary error:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate(`/${localStorage.getItem('tenant_slug') || ''}/login`);
      }
    }
    setLoading(false);
  };

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/daycare/parent-portal/reports`, { headers });
      setReports(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAttendance = async () => {
    try {
      const res = await axios.get(`${API}/daycare/parent-portal/attendance`, { headers });
      setAttendance(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API}/daycare/parent-portal/payments`, { headers });
      setPayments(res.data);
    } catch (err) { console.error(err); }
  };

  const handlePageChange = (page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === 'reports' && reports.length === 0) fetchReports();
    if (page === 'attendance' && attendance.length === 0) fetchAttendance();
    if (page === 'payments' && payments.length === 0) fetchPayments();
  };

  const handlePayNow = async (paymentId) => {
    try {
      await axios.post(`${API}/daycare/parent-portal/payments/${paymentId}/pay`, {}, { headers });
      toast.success('Payment processed successfully!');
      fetchPayments();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    }
  };

  const handleLogout = () => {
    const slug = localStorage.getItem('tenant_slug') || '';
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('tenant_slug');
    toast.success('Logged out');
    navigate(slug ? `/${slug}/login` : '/');
  };

  if (loading) {
    return <div className="pp-loading" data-testid="parent-portal-loading">Loading...</div>;
  }

  // Force password reset check
  const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
  if (authUser.must_change_password) {
    return <ForcePasswordReset onComplete={() => window.location.reload()} />;
  }

  const nav = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'children', label: 'My Children', icon: Users },
    { key: 'reports', label: 'Reports', icon: FileText },
    { key: 'attendance', label: 'Attendance', icon: Calendar },
    { key: 'payments', label: 'Payments', icon: CreditCard },
  ];

  const ppAccent = branding?.primary_color || '#2563eb';
  const ppLogoUrl = branding?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : '';
  const ppBrandName = branding?.brand_name || 'Parent Portal';

  return (
    <div className="pp-layout" data-testid="parent-portal">
      {/* Mobile overlay */}
      {mobileMenuOpen && <div className="pp-overlay" onClick={() => setMobileMenuOpen(false)} />}

      {/* Sidebar */}
      <aside className={`pp-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="pp-sidebar-header">
          {ppLogoUrl && <img src={ppLogoUrl} alt={ppBrandName} className="pp-brand-logo" data-testid="pp-sidebar-logo" />}
          <div className="pp-brand">{ppBrandName}</div>
          <button className="pp-close-btn mobile-only" onClick={() => setMobileMenuOpen(false)}><X size={20} /></button>
        </div>
        <div className="pp-user-card">
          <div className="pp-avatar" style={{ background: ppAccent }}>{user?.name?.charAt(0) || 'P'}</div>
          <div>
            <div className="pp-user-name">{user?.name}</div>
            <div className="pp-user-role">Parent</div>
          </div>
        </div>
        <nav className="pp-nav">
          {nav.map(item => {
            const Icon = item.icon;
            return (
              <button key={item.key} className={`pp-nav-link ${activePage === item.key ? 'active' : ''}`}
                style={activePage === item.key ? { background: `${ppAccent}12`, color: ppAccent, borderRightColor: ppAccent } : {}}
                onClick={() => handlePageChange(item.key)} data-testid={`pp-nav-${item.key}`}>
                <Icon size={20} /><span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="pp-sidebar-footer">
          <button className="pp-nav-link" onClick={handleLogout} data-testid="pp-logout">
            <LogOut size={20} /><span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="pp-main">
        <header className="pp-topbar">
          <button className="pp-hamburger mobile-only" onClick={() => setMobileMenuOpen(true)} data-testid="pp-hamburger">
            <Menu size={22} />
          </button>
          <h1>{nav.find(n => n.key === activePage)?.label || 'Dashboard'}</h1>
        </header>

        <div className="pp-content">
          {activePage === 'dashboard' && <DashboardView summary={summary} onNavigate={handlePageChange} />}
          {activePage === 'children' && <ChildrenView children={summary?.children || []} />}
          {activePage === 'reports' && <ReportsView reports={reports} />}
          {activePage === 'attendance' && <AttendanceView attendance={attendance} />}
          {activePage === 'payments' && <PaymentsView payments={payments} balance={summary?.outstanding_balance || 0} onPay={handlePayNow} />}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="pp-bottom-nav" data-testid="pp-bottom-nav">
        {nav.map(item => {
          const Icon = item.icon;
          return (
            <button key={item.key} className={`pp-bottom-item ${activePage === item.key ? 'active' : ''}`}
              style={activePage === item.key ? { color: ppAccent } : {}}
              onClick={() => handlePageChange(item.key)} data-testid={`pp-bnav-${item.key}`}>
              <Icon size={20} />
              <span>{item.label.replace('My ', '')}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/* ==================== DASHBOARD ==================== */
function DashboardView({ summary, onNavigate }) {
  if (!summary) return null;
  return (
    <div className="pp-dashboard" data-testid="pp-dashboard">
      {/* Children Cards */}
      <h2 className="pp-section-title">My Children</h2>
      <div className="pp-children-grid">
        {summary.children.map(child => {
          const photoSrc = child.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${child.photo_url}` : '';
          return (
            <div key={child.id} className="pp-child-card" data-testid={`pp-child-${child.id}`}>
              {photoSrc ? (
                <img src={photoSrc} alt={child.first_name} className="pp-child-photo" />
              ) : (
                <div className="pp-child-avatar">{child.first_name.charAt(0)}{child.last_name.charAt(0)}</div>
              )}
              <div className="pp-child-info">
                <h3>{child.first_name} {child.last_name}</h3>
                <span className="pp-child-meta">{child.classroom} &middot; {child.age} yrs</span>
                <span className={`pp-status-pill ${child.status}`}>{child.status}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div className="pp-quick-stats">
        <div className="pp-qstat" onClick={() => onNavigate('reports')} data-testid="pp-qstat-reports">
          <FileText size={20} />
          <span>{summary.latest_reports.length} Recent Reports</span>
          <ChevronRight size={16} />
        </div>
        <div className="pp-qstat" onClick={() => onNavigate('attendance')} data-testid="pp-qstat-attendance">
          <Calendar size={20} />
          <span>{summary.attendance_today.length} Checked In Today</span>
          <ChevronRight size={16} />
        </div>
        <div className="pp-qstat" onClick={() => onNavigate('payments')} data-testid="pp-qstat-payments">
          <CreditCard size={20} />
          <span>Balance: ${summary.outstanding_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          <ChevronRight size={16} />
        </div>
      </div>

      {/* Latest Reports */}
      {summary.latest_reports.length > 0 && (
        <>
          <h2 className="pp-section-title">Latest Reports</h2>
          <div className="pp-reports-preview">
            {summary.latest_reports.map(r => {
              const MoodIcon = MOOD_ICONS[r.mood] || Smile;
              return (
                <div key={r.id} className="pp-report-card" data-testid="pp-report-preview">
                  <div className="pp-report-header">
                    <strong>{r.child_name}</strong>
                    <span className={`pp-type-badge ${r.type}`}>{r.type}</span>
                  </div>
                  <div className="pp-report-date">{r.date} &middot; by {r.staff}</div>
                  <div className="pp-report-mood" style={{ color: MOOD_COLORS[r.mood] }}>
                    <MoodIcon size={16} /> {r.mood}
                  </div>
                  {r.activities && <p className="pp-report-text">{r.activities}</p>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ==================== CHILDREN ==================== */
function ChildrenView({ children }) {
  return (
    <div className="pp-children-view" data-testid="pp-children-view">
      {children.length === 0 ? (
        <div className="pp-empty">No children linked to your account.</div>
      ) : (
        <div className="pp-children-detail-grid">
          {children.map(child => {
            const photoSrc = child.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${child.photo_url}` : '';
            return (
              <div key={child.id} className="pp-child-detail-card" data-testid={`pp-child-detail-${child.id}`}>
                {photoSrc ? (
                  <img src={photoSrc} alt={child.first_name} className="pp-child-detail-photo" />
                ) : (
                  <div className="pp-child-detail-avatar">{child.first_name.charAt(0)}{child.last_name.charAt(0)}</div>
                )}
                <h3>{child.first_name} {child.last_name}</h3>
              <div className="pp-detail-row"><span>Classroom</span><strong>{child.classroom}</strong></div>
              <div className="pp-detail-row"><span>Age</span><strong>{child.age} years</strong></div>
              <div className="pp-detail-row"><span>Status</span><span className={`pp-status-pill ${child.status}`}>{child.status}</span></div>
              {child.allergies?.length > 0 && (
                <div className="pp-detail-row"><span>Allergies</span>
                  <div className="pp-allergy-tags">{child.allergies.map((a, i) => <span key={i} className="pp-allergy-tag">{a}</span>)}</div>
                </div>
              )}
              {child.medical_conditions && (
                <div className="pp-detail-row"><span>Medical</span><strong>{child.medical_conditions}</strong></div>
              )}
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}

/* ==================== REPORTS ==================== */
function ReportsView({ reports }) {
  return (
    <div className="pp-reports-view" data-testid="pp-reports-view">
      {reports.length === 0 ? (
        <div className="pp-empty">No reports available yet.</div>
      ) : (
        <div className="pp-timeline">
          {reports.map(r => {
            const MoodIcon = MOOD_ICONS[r.mood] || Smile;
            return (
              <div key={r.id} className="pp-timeline-item" data-testid="pp-report-item">
                <div className="pp-timeline-dot" style={{ background: r.type === 'incident' ? '#ef4444' : '#3b82f6' }} />
                <div className="pp-timeline-card">
                  <div className="pp-timeline-header">
                    <strong>{r.child_name}</strong>
                    <span className={`pp-type-badge ${r.type}`}>{r.type}</span>
                    <span className="pp-timeline-date">{r.date}</span>
                  </div>
                  <div className="pp-timeline-staff">Staff: {r.staff}</div>
                  <div className="pp-report-mood" style={{ color: MOOD_COLORS[r.mood] }}>
                    <MoodIcon size={16} /> Mood: {r.mood}
                  </div>
                  {r.activities && <div className="pp-report-section"><label>Activities</label><p>{r.activities}</p></div>}
                  {r.meals && <div className="pp-report-section"><label>Meals</label><p>{r.meals}</p></div>}
                  {r.nap_time && <div className="pp-report-section"><label>Nap Time</label><p>{r.nap_time}</p></div>}
                  {r.notes && <div className="pp-report-section"><label>Notes</label><p>{r.notes}</p></div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ==================== ATTENDANCE ==================== */
function AttendanceView({ attendance }) {
  const grouped = {};
  attendance.forEach(a => {
    if (!grouped[a.date]) grouped[a.date] = [];
    grouped[a.date].push(a);
  });
  const dates = Object.keys(grouped).sort().reverse();

  return (
    <div className="pp-attendance-view" data-testid="pp-attendance-view">
      {dates.length === 0 ? (
        <div className="pp-empty">No attendance records found.</div>
      ) : (
        dates.map(dt => (
          <div key={dt} className="pp-att-group">
            <h3 className="pp-att-date">{dt}</h3>
            {grouped[dt].map(a => (
              <div key={a.id} className="pp-att-card" data-testid="pp-att-item">
                <div className="pp-att-child">{a.child_name}</div>
                <div className="pp-att-times">
                  <span><Clock size={14} /> In: {a.check_in_time || '—'}</span>
                  <span><Clock size={14} /> Out: {a.check_out_time || '—'}</span>
                </div>
                <span className="pp-att-status" style={{ color: STATUS_COLORS[a.status] || '#6b7280' }}>{(a.status || '').replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

/* ==================== PAYMENTS ==================== */
function PaymentsView({ payments, balance, onPay }) {
  return (
    <div className="pp-payments-view" data-testid="pp-payments-view">
      {/* Balance card */}
      <div className="pp-balance-card" data-testid="pp-balance-card">
        <div className="pp-balance-label">Outstanding Balance</div>
        <div className={`pp-balance-amount ${balance > 0 ? 'due' : 'clear'}`}>
          ${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </div>
      </div>

      <h2 className="pp-section-title">Payment History</h2>
      {payments.length === 0 ? (
        <div className="pp-empty">No payments found.</div>
      ) : (
        <div className="pp-payment-list">
          {payments.map(p => (
            <div key={p.id} className="pp-payment-card" data-testid="pp-payment-item">
              <div className="pp-payment-info">
                <strong>${Number(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                <span className="pp-payment-desc">{p.description || p.type}</span>
                <span className="pp-payment-date">Due: {p.due_date || '—'} {p.paid_date ? `| Paid: ${p.paid_date}` : ''}</span>
              </div>
              <div className="pp-payment-right">
                <span className="pp-payment-status" style={{ color: STATUS_COLORS[p.status] }}>{p.status}</span>
                {(p.status === 'pending' || p.status === 'overdue') && (
                  <button className="pp-pay-btn" onClick={() => onPay(p.id)} data-testid={`pp-pay-${p.id}`}>
                    Pay Now
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
